import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbExternalLink, TbFolderSymlink, TbLink, TbStar, TbStarOff, TbTrash } from 'react-icons/tb';

import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useDashboardStore } from '@/stores';
import { Dashboard } from '@/types';

import { getWorkspaceResourceRemovalActionLabel, WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface DashboardsActionsDropdownProps {
    dashboard: Dashboard;
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
}

const DashboardsActionsDropdown: FC<DashboardsActionsDropdownProps> = ({
    dashboard,
    children,
    onOpenChange,
    open,
}): JSX.Element => {
    const [renameVisible, setRenameVisible] = useState(false);
    const [moveToFolderVisible, setMoveToFolderVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);

    const { dashboards, toggleDashboardFavorite, duplicateDashboard, toggleDashboardLock, toggleDashboardArchive } =
        useDashboardStore();
    const access = useWorkspaceResourceAccess('dashboards', dashboard?.id);
    const createAccess = useWorkspaceResourceCreateAccess('dashboards', dashboards?.length ?? 0);

    const handleToggleFavorite = async () => {
        toggleDashboardFavorite(dashboard?.id, !dashboard?.favorited);
    };

    const handleToggleLock = async () => {
        toggleDashboardLock(dashboard?.id, !dashboard?.locked);
    };

    const handleToggleArchive = async () => {
        toggleDashboardArchive(dashboard?.id, !dashboard?.archived);
    };

    const handleDuplicateDashboard = async () => {
        if (createAccess.disabled) {
            return;
        }

        duplicateDashboard(dashboard?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        {
            key: 'favorited',
            icon: dashboard?.favorited ? <TbStarOff size={'1rem'} /> : <TbStar size={'1rem'} />,
            label: dashboard?.favorited ? 'Remove from Favorites' : 'Add to Favorites',
            onClick: handleToggleFavorite,
            permission: 'update',
        },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={dashboard?.locked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', dashboard?.locked),
            onClick: handleToggleLock,
            permission: 'manage',
        },
        {
            key: 'archived',
            icon: <WorkspaceResourceStatusActionIcon active={dashboard?.archived} status='archived' />,
            label: getWorkspaceResourceStatusToggleLabel('archived', dashboard?.archived),
            onClick: handleToggleArchive,
            permission: 'manage',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard
                    .writeText(
                        `${
                            import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                        }/dashboards/${dashboard?.id}`,
                    )
                    .then(
                        () => {
                            toast('Link Copied', {
                                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                                description: dashboard?.name,
                            });
                        },
                        (error) => {
                            toast('Error Copying Link', {
                                description: dashboard?.name,
                            });

                            captureException(error);
                        },
                    );
            },
            permission: 'read',
        },
        {
            key: 'openInNewWindow',
            icon: <TbExternalLink size={'1rem'} />,
            label: 'Open in New Window',
            onClick: () => {
                window.open(`/dashboards/${dashboard?.id}`, '_blank');
            },
            permission: 'read',
        },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            disabled: createAccess.disabled,
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateDashboard,
            permission: 'read',
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/dashboards/${dashboard?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`Dashboard ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.editedName,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        {
            key: 'moveToFolder',
            icon: <TbFolderSymlink size='1rem' />,
            label: 'Move to Folder',
            onClick: () => {
                setMoveToFolderVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: getWorkspaceResourceRemovalActionLabel('dashboards'),
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'manage',
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                history={{ resourceId: dashboard.id, resourceType: 'dashboards', title: dashboard.name }}
                items={items}
                onOpenChange={onOpenChange}
                open={open}
                resourceLabel='Dashboard'
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Action Modals */}
            <RenameResourceModal
                open={renameVisible}
                setOpen={setRenameVisible}
                resource={dashboard}
                singularResource='dashboard'
            />
            <MoveResourceToFolderModal
                open={moveToFolderVisible}
                setOpen={setMoveToFolderVisible}
                resource={dashboard}
                singularResource='dashboard'
            />
            <DeleteResourceModal
                open={deleteVisible}
                setOpen={setDeleteVisible}
                resource={dashboard}
                singularResource='dashboard'
            />
        </>
    );
};

export default DashboardsActionsDropdown;
